import { ReactNode, useRef } from 'react';
import classNames from 'classnames';
import BootstrapModal from 'react-bootstrap/Modal';
import CloseIcon from '@mui/icons-material/Close';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';

import Button, { ButtonRef, ButtonVariant } from 'components/common/Button/Button';
import Toolbar from 'components/common/Toolbar/Toolbar';

import './Dialog.scss';

// TODO: this type should extend ToolbarProps when migrated to TypeScript
export type CustomDialogHeaderOrFooter = {
  items?: Array<ReactNode>;
  leftItems?: Array<ReactNode>;
  rightItems?: Array<ReactNode>;
  fillItemIndex?: number;
  className?: string;
  omit?: boolean;
};

export type Callback = () => void;

type DialogProps = {
  children: ReactNode;
  show: boolean;
  close: () => void;
  closeBeforeActions?: boolean;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  title?: string;
  header?: CustomDialogHeaderOrFooter;
  footer?: CustomDialogHeaderOrFooter;
  errorMsg?: string;
  okCallback?: Callback;
  cancelCallback?: Callback;
  noAutoFocus?: boolean;
  focusButtonRef?: ButtonRef;
  size?: 'sm' | 'lg' | 'xl';
  okButtonDisabled?: boolean;
  okButtonText?: string;
  cancelButtonText?: string;
  onShowCallback?: () => void;
  onEnterCallback?: () => void;
  enforceFocus?: boolean;
  testId?: ELEMENT;
  scrollable?: boolean;
};

/**
 * Dialog is a component for standardizing Scalyr modal dialogs.
 *
 * By default it provides a header with a title and a close button, and a footer with OK and Cancel buttons. The default behavior for both
 * is to close the dialog, but the caller will usually want to provide at least a callback for the OK button.
 *
 * Alternatively, the caller can provide custom content for the header and/or footer. The header and footer are expressed as toolbars,
 * so the custom content is passed as Toolbar props. There is one additional non-Toolbar prop that matters:
 *
 *       omit          if true, omit the header/footer
 *
 * @prop children             The body content of the dialog
 * @prop show                 Flag indicating that the dialog is open
 * @prop close                Calling this function should set the show flag to false
 * @prop closeBeforeActions   A convinience setting that closes the dialog before calling callbacks (e.g. `doOk`, `doCancel`)
 * @prop className            Custom class to add to Dialog
 * @prop headerClassName      Custom header class
 * @prop bodyClassName        Custom body class
 * @prop footerClassName      Custom footer class
 * @prop title                Title text for the dialog.
 * @prop header               Custom header props
 * @prop footer               Custom footer props
 * @prop errorMsg             Error message to show user
 * @prop okCallback           Function to run if the user clicks the OK button
 * @prop cancelCallback       Function to run if the user clicks the Cancel button
 * @prop noAutoFocus          Set this to true if you don't want the first enabled text input or textarea to get the focus
 * @prop focusButtonRef       React ref for button to set focus to when dialog is shown (usually the OK button in a custom footer)
 * @prop size                 Max dialog size: 'sm' 'md' 'lg'
 * @prop okButtonDisabled     Flag indicating that the ok button is disabled
 * @prop okButtonText         Alternate text for the OK button
 * @prop cancelButtonText     Alternate text for the Cancel button
 * @prop onShowCallback       Function to run when dialog is shown
 * @prop onEnterCallback      Function to run when Enter is typed (requires a dialog element to have focus)
 * @prop enforceFocus         Boolean to keep focus on the dialog as long as it's open
 * @prop testId               String to use as base for test IDs
 * @prop scrollable           Boolean to allow scrolling of modal body instead of entire modal overflowing
 */

function Dialog({
  children,
  show,
  close,
  closeBeforeActions = true, // a convenience setting that closes the dialog before calling callbacks (e.g. `doOk`, `doCancel`)
  className,
  headerClassName,
  bodyClassName,
  footerClassName,
  title,
  header,
  footer,
  errorMsg,
  okCallback,
  cancelCallback,
  noAutoFocus,
  focusButtonRef,
  size,
  okButtonDisabled = false,
  okButtonText = 'OK',
  cancelButtonText = 'Cancel',
  onShowCallback,
  onEnterCallback,
  enforceFocus = true,
  testId,
  scrollable = true,
}: DialogProps): JSX.Element {
  const refElement = useRef<HTMLDivElement>(null);
  const okButtonRef = useRef<ButtonRef>(null) as ButtonRef;

  // returns a function that closes the dialog before calling the given function
  const closeBefore =
    (callback?: Callback): (() => void) =>
    (): void => {
      if (closeBeforeActions) {
        close();
      }
      if (callback) {
        callback();
      }
    };

  // Ugh, if only react-bootstrap's autoFocus worked. It doesn't, and there are a number of open issues about it on github, so here's our DIY...
  const onShow = (): void => {
    if (!noAutoFocus) {
      let focusSet = false;
      if (refElement.current) {
        const formWrapper = refElement.current as HTMLDivElement;
        const nodeList = formWrapper.querySelectorAll('input, textarea');
        for (let index = 0; index < nodeList.length; index += 1) {
          if (nodeList[index].tagName.toLowerCase() === 'textarea') {
            const textArea = nodeList[index] as HTMLTextAreaElement;
            if (!textArea.disabled) {
              textArea.focus();
              textArea.select();
              focusSet = true;
              break;
            }
          } else if (nodeList[index].tagName.toLowerCase() === 'input') {
            const input = nodeList[index] as HTMLInputElement;
            if (input.type === 'text' && !input.disabled) {
              input.focus();
              input.select();
              focusSet = true;
              break;
            }
          }
        }
      }

      // if we didn't find an input, try to set focus to the OK button; we won't get keyboard events unless
      // something in the dialog has focus
      if (!focusSet && refElement.current) {
        // @ts-expect-error
        if (focusButtonRef?.current?.focus) {
          // @ts-expect-error
          focusButtonRef?.current?.focus();
          // @ts-expect-error
        } else if (okButtonRef?.current?.focus) {
          // @ts-expect-error
          okButtonRef.current.focus();
        }
      }
    }

    if (onShowCallback) {
      onShowCallback();
    }
  };

  const doCancel = closeBefore(cancelCallback);
  const doOk = closeBefore(okCallback);

  const dialogClassName = classNames('Dialog', className);

  // returns toolbar with title and close button
  const getStandardHeader = (): ReactNode => {
    const titleTestId = testId ? createTestId(testId, 'title') : undefined;
    const closeTestId = testId ? createTestId(testId, 'close') : undefined;
    return (
      <>
        <BootstrapModal.Title className="Dialog__Title" data-test-id={titleTestId}>
          {title}
        </BootstrapModal.Title>
        <div className="Dialog__Close">
          <CloseIcon onClick={doCancel} data-test-id={closeTestId} />
        </div>
      </>
    );
  };

  // returns toolbar with OK and Cancel buttons, placed at the right
  const getStandardFooter = (): ReactNode => {
    const okTestId = testId ? createTestId(testId, 'ok') : undefined;
    const cancelTestId = testId ? createTestId(testId, 'cancel') : undefined;
    const buttons = [
      <Button
        variant={ButtonVariant.PRIMARY}
        onClick={doOk}
        disabled={okButtonDisabled}
        testId={okTestId}
        buttonRef={okButtonRef}
      >
        {okButtonText}
      </Button>,
      <Button onClick={doCancel} testId={cancelTestId}>
        {cancelButtonText}
      </Button>,
    ];
    return <Toolbar rightItems={buttons} />;
  };

  // generate a header or footer based on config (if provided)
  const getHeaderOrFooter = (config?: CustomDialogHeaderOrFooter, getStandardContent?: () => ReactNode): ReactNode => {
    if (config) {
      if (config.omit) {
        return null;
      }
      const { omit, ...toolbarProps } = config;
      return <Toolbar {...toolbarProps} />;
    }
    return getStandardContent ? getStandardContent() : undefined;
  };

  // if user types Enter, run provided callback
  const onKeyDown = (event: KeyboardEvent): void => {
    const key = event.keyCode;
    const isTextAreaEvent = (event.target as HTMLInputElement)?.type === 'textarea'; // Used to ignore enter key presses when typing in a textarea input
    if (key === K.KEY_CODE.ENTER && !isTextAreaEvent) {
      if (onEnterCallback && !okButtonDisabled) {
        closeBefore(onEnterCallback)();
      }
      event.preventDefault();
    }
  };

  const headerContent = getHeaderOrFooter(header, getStandardHeader);
  const footerContent = getHeaderOrFooter(footer, getStandardFooter);

  const dialogBodyTestId = testId ? createTestId(testId, 'body') : undefined;
  const errorTestId = testId ? createTestId(testId, 'error') : undefined;

  const errorEl = errorMsg ? (
    <div className="Dialog__Error" data-test-id={errorTestId}>
      {errorMsg}
    </div>
  ) : null;

  return (
    <BootstrapModal
      dialogClassName={dialogClassName}
      show={show}
      onHide={closeBefore(cancelCallback)}
      onShow={onShow}
      backdrop
      keyboard
      size={size}
      autoFocus
      centered
      onKeyDown={(ev: KeyboardEvent): void => onKeyDown(ev)}
      data-test-id={testId}
      enforceFocus={enforceFocus}
      scrollable={scrollable}
    >
      <BootstrapModal.Header className={classNames('Dialog__Header', headerClassName)}>
        {headerContent}
      </BootstrapModal.Header>
      {errorEl}
      <BootstrapModal.Body className={classNames('Dialog__Body', bodyClassName)} data-test-id={dialogBodyTestId}>
        <div ref={refElement}>{children}</div>
      </BootstrapModal.Body>
      {!footer?.omit ? (
        <BootstrapModal.Footer className={classNames('Dialog__Footer', footerClassName)}>
          {footerContent}
        </BootstrapModal.Footer>
      ) : null}
    </BootstrapModal>
  );
}

export default Dialog;
